import { generateImage, listHistory, listShopifyProducts, pushToShopify, type HistoryItem, type ReferenceImage, type ShopifyProduct } from '../api';

const ALLOWED_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/heic', 'image/heif'];
const MAX_REFS = 14;
const MAX_REF_BYTES = 7 * 1024 * 1024;
const MAX_TOTAL_BYTES = 20 * 1024 * 1024; // base64 inflates ~33% — keep the JSON body under Cloud Run's 32 MB ingress cap

interface RefImage extends ReferenceImage { preview: string; name: string; }

export function renderGenerator(host: HTMLElement, opts: { isAdmin?: boolean } = {}) {
  const isAdmin = opts.isAdmin === true;
  host.innerHTML = `
    <style>
      .refs { margin-top: 14px; }
      .ref-drop { border: 1px dashed var(--border); border-radius: 8px; padding: 14px; text-align: center; color: var(--text-dim); font-size: 13px; cursor: pointer; transition: border-color .15s, background .15s; }
      .ref-drop:hover, .ref-drop.drag { border-color: var(--accent); background: var(--panel-2); color: var(--text); }
      .ref-drop b { color: var(--accent); font-weight: 600; }
      .ref-thumbs { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 10px; }
      .ref-thumb { position: relative; width: 64px; height: 64px; }
      .ref-thumb img { width: 64px; height: 64px; object-fit: cover; border-radius: 8px; border: 1px solid var(--border); }
      .ref-del { position: absolute; top: -6px; right: -6px; width: 20px; height: 20px; border-radius: 50%; border: 0; background: var(--danger); color: #fff; font-size: 13px; line-height: 1; cursor: pointer; display: flex; align-items: center; justify-content: center; padding: 0; }
      .ref-hint { font-size: 11px; color: var(--text-dim); margin-top: 8px; }
      .push-panel { margin-top: 12px; padding: 12px; border: 1px solid var(--border); border-radius: 8px; background: var(--panel-2); display: flex; flex-wrap: wrap; gap: 8px; align-items: center; }
      .push-panel select { flex: 1; min-width: 160px; background: var(--panel); color: var(--text); border: 1px solid var(--border); border-radius: 8px; padding: 8px 10px; font-family: inherit; font-size: 13px; }
      .push-panel .btn { white-space: nowrap; }
      .push-hint { flex-basis: 100%; font-size: 11px; color: var(--text-dim); }
      .hist-push { border: 1px solid var(--border); background: var(--panel-2); color: var(--text); border-radius: 6px; padding: 4px 10px; font-size: 12px; cursor: pointer; white-space: nowrap; }
      .hist-push:hover { border-color: var(--accent); }
      .hist-push:disabled { opacity: .6; cursor: default; }
      .pushed-tag { color: var(--ok, #4caf50); font-size: 12px; white-space: nowrap; }
      .toasts { position: fixed; bottom: 18px; right: 18px; display: flex; flex-direction: column; gap: 8px; z-index: 50; }
      .toast { max-width: 360px; padding: 12px 14px; border-radius: 8px; font-size: 13px; box-shadow: 0 4px 16px rgba(0,0,0,.35); border: 1px solid var(--border); background: var(--panel); color: var(--text); }
      .toast.ok { border-color: var(--ok, #4caf50); }
      .toast.bad { border-color: var(--danger); }
      .toast a { color: var(--accent); }
    </style>
    <div class="generator">
      <section class="panel">
        <h3>Prompt</h3>
        <textarea id="prompt" placeholder="Describe the image to create — or, with reference images, how to edit them (e.g. 'put this product on a marble kitchen counter, soft morning light')..."></textarea>
        <div class="refs">
          <div class="ref-drop" id="refDrop">
            <b>+ Add reference images</b> &middot; click or drag &amp; drop
            <input type="file" id="refInput" accept="${ALLOWED_TYPES.join(',')}" multiple hidden />
          </div>
          <div class="ref-thumbs" id="refThumbs"></div>
          <div class="ref-hint">Optional — up to ${MAX_REFS} images to edit or combine (PNG/JPEG/WebP/HEIC, max 7&nbsp;MB each). The model uses them as a visual reference.</div>
        </div>
        <div style="margin-top:12px; display:flex; gap:8px;">
          <button class="btn" id="genBtn">Generate</button>
          <span style="flex:1"></span>
          <span class="meta" style="margin:0;">
            <span>Model: <b>Nano Banana Pro</b></span>
            <span>1024×1024</span>
          </span>
        </div>
        <div class="meta" id="meta"></div>
        <div id="err"></div>
      </section>
      <section class="panel result">
        <h3>Result</h3>
        <div id="result"><div class="empty-state">Generated image will appear here.</div></div>
        <div id="pushPanel"></div>
      </section>
    </div>
    <section class="history">
      <h3 style="font-size:13px; text-transform:uppercase; color:var(--text-dim); letter-spacing:0.5px;">Recent generations</h3>
      <div id="history"><div class="empty-state">No history yet.</div></div>
    </section>
  `;

  const $ = <T extends HTMLElement = HTMLElement>(id: string) => host.querySelector<T>(`#${id}`)!;
  const btn = $<HTMLButtonElement>('genBtn');
  const prompt = $<HTMLTextAreaElement>('prompt');
  const result = $('result');
  const meta = $('meta');
  const err = $('err');
  const history = $('history');
  const drop = $('refDrop');
  const fileInput = $<HTMLInputElement>('refInput');
  const thumbs = $('refThumbs');
  const pushPanel = $('pushPanel');

  const refs: RefImage[] = [];
  // Full bytes of the freshest generation — pushes from the result panel send
  // these along so the proxy never depends on the (possibly truncated)
  // Firestore history copy.
  let lastGen: { requestId: string; imageBase64: string } | null = null;
  let productsPromise: Promise<ShopifyProduct[]> | null = null;

  function showToast(html: string, ok: boolean) {
    let box = host.querySelector<HTMLElement>('.toasts');
    if (!box) { box = document.createElement('div'); box.className = 'toasts'; host.appendChild(box); }
    const toast = document.createElement('div');
    toast.className = `toast ${ok ? 'ok' : 'bad'}`;
    toast.innerHTML = html;
    box.appendChild(toast);
    setTimeout(() => toast.remove(), 9000);
  }

  function pushSuccessToast(r: { fileGid: string; productMediaId: string | null; productAttachError: string | null; adminFilesUrl: string }) {
    const attachNote = r.productMediaId
      ? ' and attached to the product'
      : r.productAttachError
        ? ` — but the product attach failed: ${escapeHtml(r.productAttachError)}`
        : '';
    showToast(
      `Image pushed to Shopify Files${attachNote}. <a href="${escapeHtml(r.adminFilesUrl)}" target="_blank" rel="noopener">Open Shopify Admin → Files</a>`,
      !r.productAttachError,
    );
  }

  async function loadProducts(): Promise<ShopifyProduct[]> {
    // Lazy + cached — the picker only costs a Shopify call the first time.
    productsPromise ??= listShopifyProducts().catch((e: unknown) => {
      productsPromise = null;
      throw e;
    });
    return productsPromise;
  }

  function renderPushPanel() {
    if (!isAdmin || !lastGen) { pushPanel.innerHTML = ''; return; }
    pushPanel.innerHTML = `
      <div class="push-panel">
        <select id="productPick"><option value="">Files only (no product)</option></select>
        <button class="btn" id="pushBtn">Push to Shopify</button>
        <div class="push-hint">Uploads this image to the studio.opsagents.agency store — Shopify Admin → Content → Files. Pick a product to also attach it to that product's media.</div>
      </div>
    `;
    const pick = pushPanel.querySelector<HTMLSelectElement>('#productPick')!;
    const pushBtn = pushPanel.querySelector<HTMLButtonElement>('#pushBtn')!;

    loadProducts()
      .then((products) => {
        for (const p of products) {
          const opt = document.createElement('option');
          opt.value = p.id;
          opt.textContent = p.status === 'ACTIVE' ? p.title : `${p.title} (${p.status.toLowerCase()})`;
          pick.appendChild(opt);
        }
      })
      .catch((e: unknown) => {
        showToast(`Product list unavailable: ${escapeHtml(e instanceof Error ? e.message : String(e))}. You can still push to Files.`, false);
      });

    pushBtn.addEventListener('click', async () => {
      if (!lastGen) return;
      pushBtn.disabled = true; pushBtn.textContent = 'Pushing…';
      try {
        const r = await pushToShopify({
          generationId: lastGen.requestId,
          imageBase64: lastGen.imageBase64,
          productId: pick.value || undefined,
        });
        pushSuccessToast(r);
        await refreshHistory();
      } catch (e: unknown) {
        showToast(escapeHtml(e instanceof Error ? e.message : String(e)), false);
      } finally {
        pushBtn.disabled = false; pushBtn.textContent = 'Push to Shopify';
      }
    });
  }

  function renderThumbs() {
    thumbs.innerHTML = refs.map((r, i) =>
      `<div class="ref-thumb"><img src="${r.preview}" alt="${escapeHtml(r.name)}" title="${escapeHtml(r.name)}"><button type="button" class="ref-del" data-idx="${i}" title="Remove">×</button></div>`
    ).join('');
    thumbs.querySelectorAll<HTMLButtonElement>('.ref-del').forEach((b) => {
      b.addEventListener('click', () => { refs.splice(Number(b.dataset.idx), 1); renderThumbs(); });
    });
  }

  function showError(msg: string) {
    err.innerHTML = `<div class="error">${escapeHtml(msg)}</div>`;
  }

  async function addFiles(files: FileList | null) {
    if (!files) return;
    err.innerHTML = '';
    let total = refs.reduce((n, r) => n + (r.data.length * 3) / 4, 0);
    for (const file of Array.from(files)) {
      if (refs.length >= MAX_REFS) { showError(`Maximum ${MAX_REFS} reference images.`); break; }
      if (!ALLOWED_TYPES.includes(file.type)) { showError(`${file.name}: unsupported type (${file.type || 'unknown'}).`); continue; }
      if (file.size > MAX_REF_BYTES) { showError(`${file.name}: larger than 7 MB.`); continue; }
      if (total + file.size > MAX_TOTAL_BYTES) { showError('Combined reference images are too large (max ~20 MB total). Remove one or use smaller files.'); break; }
      total += file.size;
      try {
        const dataUrl = await readAsDataUrl(file);
        const m = dataUrl.match(/^data:([^;]+);base64,(.+)$/);
        if (!m) { showError(`${file.name}: could not read file.`); continue; }
        refs.push({ mime_type: m[1], data: m[2], preview: dataUrl, name: file.name });
      } catch {
        showError(`${file.name}: could not read file.`);
      }
    }
    renderThumbs();
  }

  drop.addEventListener('click', () => fileInput.click());
  fileInput.addEventListener('change', () => { addFiles(fileInput.files); fileInput.value = ''; });
  drop.addEventListener('dragover', (e) => { e.preventDefault(); drop.classList.add('drag'); });
  drop.addEventListener('dragleave', () => drop.classList.remove('drag'));
  drop.addEventListener('drop', (e) => {
    e.preventDefault();
    drop.classList.remove('drag');
    addFiles(e.dataTransfer?.files ?? null);
  });

  btn.addEventListener('click', async () => {
    const text = prompt.value.trim();
    if (!text) { showError('Enter a prompt.'); return; }
    btn.disabled = true; btn.textContent = 'Generating…';
    err.innerHTML = ''; meta.innerHTML = '';
    result.innerHTML = '<div class="empty-state">Working…</div>';
    try {
      const t0 = performance.now();
      const r = await generateImage(text, refs.map(({ mime_type, data }) => ({ mime_type, data })));
      const wall = Math.round(performance.now() - t0);
      result.innerHTML = `<img src="data:${r.mimeType};base64,${r.imageBase64}" alt="">`;
      lastGen = { requestId: r.requestId, imageBase64: r.imageBase64 };
      renderPushPanel();
      meta.innerHTML = `
        <span>Cost: <b>$${r.cost.toFixed(3)}</b></span>
        <span>Server: <b>${r.latencyMs}ms</b></span>
        <span>Wall: <b>${wall}ms</b></span>
        ${refs.length ? `<span>Refs: <b>${refs.length}</b></span>` : ''}
        <span>req: <b>${r.requestId.slice(0, 8)}</b></span>
      `;
      await refreshHistory();
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      showError(msg);
      result.innerHTML = '<div class="empty-state">Generation failed.</div>';
      lastGen = null;
      renderPushPanel();
    } finally {
      btn.disabled = false; btn.textContent = 'Generate';
    }
  });

  async function refreshHistory() {
    try {
      const items = await listHistory();
      renderHistory(history, items, isAdmin);
      wireHistoryPush();
    } catch (e) {
      console.warn('[history] refresh failed', e);
    }
  }

  function wireHistoryPush() {
    history.querySelectorAll<HTMLButtonElement>('.hist-push').forEach((b) => {
      b.addEventListener('click', async () => {
        const id = b.dataset.id!;
        const label = b.textContent;
        b.disabled = true; b.textContent = 'Pushing…';
        try {
          // History pushes use the bytes stored in Firestore; the proxy rejects
          // truncated stored copies with a "re-generate and push" message.
          const r = await pushToShopify({ generationId: id });
          pushSuccessToast(r);
          await refreshHistory();
        } catch (e: unknown) {
          showToast(escapeHtml(e instanceof Error ? e.message : String(e)), false);
          b.disabled = false; b.textContent = label;
        }
      });
    });
  }

  refreshHistory();
}

function readAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result));
    r.onerror = () => reject(r.error ?? new Error('read failed'));
    r.readAsDataURL(file);
  });
}

function renderHistory(host: HTMLElement, items: HistoryItem[], isAdmin: boolean) {
  if (!items.length) {
    host.innerHTML = '<div class="empty-state">No history yet.</div>';
    return;
  }
  host.innerHTML = items.map((it) => `
    <div class="history-item" data-testid="generation-history-item">
      ${it.thumbBase64
        ? `<img src="data:${it.mimeType};base64,${it.thumbBase64}" alt="">`
        : '<div style="width:56px;height:56px;background:var(--panel-2);border-radius:6px"></div>'}
      <div class="prompt">${escapeHtml(it.prompt)}</div>
      ${it.shopify
        ? '<span class="pushed-tag" title="Already pushed to Shopify Files">✓ Shopify</span>'
        : isAdmin
          ? `<button type="button" class="hist-push" data-id="${escapeHtml(it.id)}">Push to Shopify</button>`
          : ''}
      <div class="when">${formatWhen(it.createdAt)}</div>
    </div>
  `).join('');
}

function formatWhen(ms: number): string {
  const diff = Date.now() - ms;
  if (diff < 60_000) return 'just now';
  if (diff < 3600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3600_000)}h ago`;
  return new Date(ms).toLocaleDateString();
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
  }[c]!));
}
